import { defineComponent, h, onMounted } from 'vue'

import { QEditor } from 'quasar'

import { bsComponentProps, useBsComponent } from '../useBsComponent'
import { renderBsField } from '../field/BsField'
import { useVm } from '../../../composables/useVm'
import { quasarHandlers } from '../quasarHandlers'
import { emitModel } from '../emitModel'

/**
 * An inline style, or a font's face or colour, however the value is quoted. In view-source mode QEditor
 * hands over the text the author typed rather than its own innerHTML, so Word's single-quoted
 * `style='...'` got past the double-quoted pattern this replaced, and readers saw Word's fonts. A font's
 * size is handled below. [sc-29116]
 */
const STYLING_ATTRIBUTE = /\s+(?:style|face|color)\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'=<>`]+)/gi

/** Only inside a tag, so a blurb that says `style="` in its text keeps it. */
const OPENING_TAG = /<[a-z][^>]*>/gi

/**
 * Superscript, subscript and the three spellings of strikethrough. No toolbar button makes them, so the api
 * unwraps them; dropping just the tags keeps their text, as it does. [sc-29116]
 */
const UNOFFERED_TAG = /<\/?(?:sup|sub|strike|s|del)\b[^>]*>/gi

/** A font's size, however quoted. It stays only if it is one the toolbar's picker sets: 1 to 7. */
const FONT_SIZE_ATTRIBUTE = /\s+size\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'=<>`]+))/gi
const EDITOR_FONT_SIZE = /^[1-7]$/

const stripTag = (tag: string): string => {
  const stripped = tag.replace(STYLING_ATTRIBUTE, '')

  return /^<font\b/i.test(stripped)
    ? stripped.replace(FONT_SIZE_ATTRIBUTE, (attribute, double, single, bare) =>
      EDITOR_FONT_SIZE.test(double ?? single ?? bare) ? attribute : '')
    : stripped
}

const stripStyling = (value: string): string =>
  value.replace(UNOFFERED_TAG, '').replace(OPENING_TAG, stripTag)

/**
 * The rich-text editor. Wraps QEditor in the bundle's field shell and strips inline styles out of
 * everything typed into it, so pasted content cannot bring a font with it, along with formatting the
 * toolbar has no button for. The api strips the same (sanitiseHtml); doing it here as well means the
 * editor never shows formatting that will not be saved.
 *
 * `created` set the heading icons rather than `mounted` because QEditor builds its toolbar in its own
 * `created`; the ordering is preserved here — a `setup` body runs before any child is created, which is
 * earlier still and therefore safe.
 */
export default defineComponent({
  name: 'BsEditor',

  // See BsInput: undeclared DOM-named events are bound twice, once natively.
  emits: [ 'input', 'update:modelValue' ],

  props: {
    ...bsComponentProps,
    limitedToolbar: { type: Boolean, default: false }
  },

  setup (props, { emit }) {
    const vm = useVm()

    vm.$q.iconSet.editor.heading1 = 'mdi-format-header-1'
    vm.$q.iconSet.editor.heading2 = 'mdi-format-header-2'
    vm.$q.iconSet.editor.heading3 = 'mdi-format-header-3'
    vm.$q.iconSet.editor.heading4 = 'mdi-format-header-4'
    vm.$q.iconSet.editor.heading5 = 'mdi-format-header-5'
    vm.$q.iconSet.editor.heading6 = 'mdi-format-header-6'

    onMounted(() => {
      // this is used by quasar too, but is executed on created() hook
      // which is a bit too early for us
      document.execCommand('defaultParagraphSeparator', false, 'p')
    })

    return { ...useBsComponent({ vm, props, emit }) }
  },

  methods: {
    getEditorToolbar () {
      const allowedTags = this.limitedToolbar ? ['p'] : ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p']
      const allowedFontSizes = this.limitedToolbar
        ? [
          {
            label: this.$q.lang.editor.fontSize,
            icon: this.$q.iconSet.editor.fontSize,
            fixedLabel: true,
            fixedIcon: true,
            list: 'no-icons',
            options: [
              'size-1',
              'size-2',
              'size-3',
              'size-4',
              'size-5',
              'size-6',
              'size-7'
            ]
          }
        ] : void 0
      const allowedTagStyles = ['bold', 'italic', 'underline']
      const allowedListTypes = ['unordered', 'ordered']
      const allowedOtherTags = ['hr', 'removeFormat']
      const allowedViewSource = ['viewsource']
      // don't add empty or undefined items to the array
      // quasar validation will kick in if we do so
      const toolbar = []

      toolbar.push(allowedTags)

      if (allowedFontSizes) {
        toolbar.push(allowedFontSizes)
      }

      toolbar.push(allowedTagStyles)
      toolbar.push(allowedListTypes)
      toolbar.push(allowedOtherTags)
      // `allowedViewSource` is a literal, so the class's `if` around this push could never be false.
      toolbar.push(allowedViewSource)

      return toolbar
    },

    /** Renamed from `__getControl`: it is an argument to `renderBsField` now, not an inherited hook. */
    getControl () {
      // `componentValue`, not `$attrs.value`: a `v-model` caller sends `modelValue`, and reading the
      // old name gave the editor an empty document however much the caller had bound to it.
      if (this.componentValue === null) {
        emitModel(this, '')
        return void 0
      }

      const props = {
        toolbar: this.getEditorToolbar(),
        value: this.componentValue || '',
        paragraphTag: 'p'
      }

      // Emitting straight through skips the validation getListeners' own `input` handler runs on every
      // change: with auto-save on, the editor validates on blur. Without auto-save getListeners swaps its
      // handler in for this one, which is how styles used to get through there - so the stripping goes in
      // front of whichever handler it hands back. [sc-29116]
      const { input, ...listeners } = this.getListeners({ input: (value: string) => emitModel(this, value) })

      /**
       * A wrapper carrying `data-model`, which is the one component in the bundle that cannot hand it
       * to the Quasar component inside.
       *
       * Quasar 2's QEditor sets `inheritAttrs: false` and applies what falls through to its *content*
       * div -- the contenteditable itself. Quasar 1 put it on the editor's root. So the attribute
       * landed on the very element `cy.component('bio').find('[contenteditable="true"]')` then
       * searches inside, and the editor's own tests found nothing. The field root is no good either:
       * `autoSaves()` walks UP from the selected element to `.q-field__inner`, so the marker has to
       * sit below that and above the contenteditable, which is exactly where the editor's root was.
       */
      return h('div', {
        class: 'bs-editor__container full-width',
        'data-model': this.dataModel
      }, [
        h(QEditor, {
          class: ['bs-editor full-width', this.getClasses()],
          ...this.getRenderProps(props),
          ...quasarHandlers({ ...listeners, input: (value: string) => input(stripStyling(value)) }),
          ...this.getAttributes(void 0, true)
        }, { default: () => this.slot(this, 'default') })
      ])
    }
  },

  render () {
    return renderBsField(this, 'bs-editor', this.getControl())
  }
})
